import { Directive, ElementRef, inject, Input, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { baseStatusStyles } from '../statusUIIcons';

/**
 * Emphasis directive for highlighting critical text (out-of-stock, overdue, 0, etc.)
 * Similar to chips but simpler: no icons, minimal padding, just color-coded text emphasis
 * 
 * Usage:
 * <span [appEmphasis]="'out-of-stock'">Out of Stock</span>
 * <span [appEmphasis]="'0'" [fontSize]="'12px'">0</span>
 * <span [appEmphasis]="'overdue'">Overdue</span>
 */
@Directive({
    selector: '[appEmphasis]',
    standalone: true
})
export class EmphasisDirective {
    private prevStatusClass: string | null = null;
    private _status = '';
    private _fontSize = '';
    private _showBorder = true;
    private _opacity = '0.9';

    @Input('appEmphasis')
    set status(value: string | number | null | undefined) {
        this._status = (value ?? '').toString().trim();
        this.render();
    }

    @Input()
    set fontSize(value: string | null | undefined) {
        this._fontSize = (value ?? '').toString();
        this.render();
    }

    @Input()
    set showBorder(value: boolean | null | undefined) {
        this._showBorder = value ?? true;
        this.render();
    }

    @Input()
    set opacity(value: string | number | null | undefined) {
        this._opacity = value ? String(value) : '0.9';
        this.render();
    }

    private el = inject(ElementRef<HTMLElement>);
    private renderer = inject(Renderer2);

    private render() {
        const host = this.el.nativeElement;
        const normalized = this._status.toLowerCase();

        // Map common emphasis states to status colors if not in baseStatusStyles
        const emphasisColorMap: { [key: string]: string } = {
            'grey': 'grey',
            '0': 'gray',
            'zero': 'gray',
            'out-of-stock': 'red',
            'out of stock': 'red',
            'overdue': 'orange',
            'critical': 'deep-red',
            'low': 'orange',
            'expired': 'orange',
            'depleted': 'red',
            'insufficient': 'orange',
            'warning': 'orange',
            'error': 'red',
            'success': 'green',
            'info': 'blue',
            'virtual': 'blue',
            'telehealth': 'blue',
            'telemedicine': 'blue',
            'in-person': 'green',
            'in person': 'green',
            'high': 'green',
            'medium': 'orange',
            'none': 'gray',
            'urgent': 'red',
            'admitted': 'red',
            'discharged': 'green',
            'chief-complaint': 'purple',
            'symptom': 'orange',
            'total': 'orange',
            'free': 'green',
            'mild': 'blue',
            'moderate': 'orange',
            'severe': 'red',
            'mild-severity': 'blue',
            'moderate-severity': 'orange',
            'severe-severity': 'red'
        };

        // Base classes
        this.renderer.addClass(host, 'emphasis-badge');
        this.renderer.addClass(host, 'g-just-flex');

        // Apply minimal padding (2px vertical, 6px horizontal)
        this.renderer.setStyle(host, 'padding', '2px 6px', RendererStyleFlags2.Important);
        this.renderer.setStyle(host, 'border-radius', '3px', RendererStyleFlags2.Important);
        this.renderer.setStyle(host, 'display', 'inline-flex', RendererStyleFlags2.Important);
        this.renderer.setStyle(host, 'align-items', 'center', RendererStyleFlags2.Important);
        this.renderer.setStyle(host, 'justify-content', 'center', RendererStyleFlags2.Important);
        this.renderer.setStyle(host, 'font-weight', '500', RendererStyleFlags2.Important);
        this.renderer.setStyle(host, 'text-transform', 'capitalize', RendererStyleFlags2.Important);
        this.renderer.setStyle(host, 'white-space', 'nowrap', RendererStyleFlags2.Important);

        // Apply optional font size
        if (this._fontSize) {
            this.renderer.setStyle(host, 'font-size', this._fontSize, RendererStyleFlags2.Important);
            const lineHeight = Number(this._fontSize.match(/\d+/)?.[0] || 14) + 2;
            this.renderer.setStyle(host, 'line-height', `${lineHeight}px`, RendererStyleFlags2.Important);
        } else {
            this.renderer.setStyle(host, 'font-size', '0.75rem', RendererStyleFlags2.Important);
        }

        // Determine color from baseStatusStyles or emphasisColorMap
        const statusColor = baseStatusStyles[normalized]?.color || emphasisColorMap[normalized] || 'gray';

        // Dynamic status-* class with opacity override
        const nextStatusClass = `status-${statusColor}`;
        if (this.prevStatusClass && this.prevStatusClass !== nextStatusClass) {
            this.renderer.removeClass(host, this.prevStatusClass);
        }
        this.renderer.addClass(host, nextStatusClass);
        this.prevStatusClass = nextStatusClass;

        // Apply/remove border based on showBorder input
        if (this._showBorder) {
            this.renderer.setStyle(host, 'border', '1px solid', RendererStyleFlags2.Important);
        } else {
            this.renderer.setStyle(host, 'border', 'none', RendererStyleFlags2.Important);
        }

        // Apply opacity if specified
        if (this._opacity !== '1' && this._opacity !== '1.0') {
            this.renderer.setStyle(host, 'opacity', this._opacity, RendererStyleFlags2.Important);
        }
    }
}
